<script setup lang="ts">
import { computed, ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppTabs } from '@libs/ui';
import type { AppTabConfig } from '@libs/ui';

import type { PeekPriceRoomDetails } from '../../models';

import PeekPriceSingleRoom from './PeekPriceSingleRoom.vue';

interface PeekPriceHotelOnlyRoomsProps {
  rooms: PeekPriceRoomDetails[];
}

const props = defineProps<PeekPriceHotelOnlyRoomsProps>();

const { t } = i18n.global;

const userSelectedTabKey = ref<string | undefined>(undefined);

const tabs = computed<AppTabConfig[]>(() =>
  props.rooms.map((_: PeekPriceRoomDetails, index: number) => ({
    title: `${t('planner.pax.rooms')} ${index + 1}`,
    key: `room${index + 1}`,
  }))
);

const selectedTabKey = computed<string | undefined>(() =>
  userSelectedTabKey.value === undefined
    ? tabs.value[0]?.key
    : userSelectedTabKey.value
);

const visibleRoom = computed<PeekPriceRoomDetails | undefined>(() => {
  const index = tabs.value.findIndex(
    (tab: AppTabConfig) => tab.key === userSelectedTabKey.value
  );
  return index < 0 ? props.rooms[0] : props.rooms[index];
});

function onChangeTab(key: string): void {
  userSelectedTabKey.value = key;
}
</script>

<template>
  <div class="peek-price-hotel-only-rooms">
    <PeekPriceSingleRoom
      v-if="rooms.length === 1 && visibleRoom"
      :room-description="visibleRoom.description"
      :board-basis-code="visibleRoom.boardBasisCode"
    />

    <template v-else>
      <AppTabs
        v-if="selectedTabKey"
        :tabs="tabs"
        :selected-tab-key="selectedTabKey"
        type="compact"
        class="room-tabs"
        @tab-click="onChangeTab"
      />
      <PeekPriceSingleRoom
        v-if="rooms.length > 1 && visibleRoom"
        :room-description="visibleRoom.description"
        :board-basis-code="visibleRoom.boardBasisCode"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.peek-price-hotel-only-rooms {
  .room-tabs {
    margin-bottom: var(--spacing-12);
  }
}
</style>
